import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { type TFunction } from "i18next";
import { ApiError } from "@njbsoft/api-client";
import { BREAKPOINT_SM_QUERY, useMediaQuery } from "@njbsoft/form-ui";
import { type components } from "@njbsoft/types/field";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  EmptyValue,
  PageHeader,
  Skeleton,
  SkeletonTable,
  type BadgeVariant,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { FieldAccessEnded } from "@/components/tester/chrome/field-access-ended";
import { KitFormDialog } from "@/components/tester/test-kits/kit-form-dialog";
import { CalibrationFormDialog } from "@/components/tester/test-kits/calibration-form-dialog";
import { kitBadge } from "@/components/tester/execution/kit-badge";
import { useMyTestKits } from "@/hooks/use-my-test-kits";
import { useDeleteMyTestKit } from "@/hooks/use-my-kit-mutations";
import { useIsOnline } from "@/hooks/use-is-online";
import { useToast } from "@/hooks/use-toast";
import { useCompanyStore } from "@/store";
import { formatDateOnly } from "@/lib/dates";

type TestKit = components["schemas"]["TestKit"];

export const Route = createFileRoute("/_company/test-kits/")({
  component: TesterTestKitsPage,
});

const columnHelper = createColumnHelper<TestKit>();

/** My kits: Kit, Type, Status, Calibration, Last used, Actions. */
export const MY_KITS_COLUMN_COUNT = 6;

const EXPIRING_BANNER_WINDOW_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type KitGroupKey = "mine" | "companies";

const GROUP_ORDER: KitGroupKey[] = ["mine", "companies"];

/**
 * Map one kit onto its ownership group. The wire union still names
 * `utility_org` (the column's stored vocabulary), but the field-selectable
 * seam drops those rows server-side before they ever reach this page, so no
 * arm maps one: a row of an unmapped kind lands in no group rather than
 * rendering under a group it does not belong to. The exclusion itself lives
 * ONLY in the Go repository — never here.
 */
function groupForKit(kit: TestKit): KitGroupKey | undefined {
  if (kit.owner_kind === "tester") return "mine";
  if (kit.owner_kind === "testing_company") return "companies";
  return undefined;
}

/**
 * Split the kit list into the two ownership groups, in a fixed order, with
 * empty groups dropped entirely rather than rendered as a bare header. Server
 * order is preserved WITHIN each group at first render — the table's sorting
 * is client-side over the whole (small, fully fetched) list, so a header press
 * reorders everything rather than one page of something.
 */
export function buildKitGroups(kits: TestKit[]): { group: KitGroupKey; kits: TestKit[] }[] {
  const groups: { group: KitGroupKey; kits: TestKit[] }[] = [];
  for (const group of GROUP_ORDER) {
    const inGroup = kits.filter((kit) => groupForKit(kit) === group);
    if (inGroup.length > 0) groups.push({ group, kits: inGroup });
  }
  return groups;
}

/** The row's one-line identity: "Blue-White DPC-2 · #BW-2209" (mock cell-primary). */
export function kitIdentity(kit: TestKit): string {
  const name = [kit.manufacturer, kit.model].filter(Boolean).join(" ");
  return `${name} · #${kit.serial_number}`;
}

/**
 * Date-only parse to local midnight so a YYYY-MM-DD value does not shift a day
 * in western timezones (the credential-chip convention).
 */
function parseDateOnly(value: string): Date {
  const datePart = value.split("T")[0] ?? value;
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

function daysUntil(value: string, now: Date): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((parseDateOnly(value).getTime() - today.getTime()) / MS_PER_DAY);
}

export interface ExpiringCalibration {
  kit: TestKit;
  expiresAt: string;
}

/**
 * The banner's predicate: every kit whose latest calibration expires within
 * 30 days (inclusive, from today). Already-expired calibrations are NOT
 * banner material — their danger badge in the table is the honest reading of
 * a window already closed, and the banner speaks about windows still open.
 * Soonest expiry first, so the banner's action targets the most urgent gauge.
 */
export function kitsExpiringWithin(kits: TestKit[], now: Date): ExpiringCalibration[] {
  const expiring: Array<ExpiringCalibration & { days: number }> = [];
  for (const kit of kits) {
    const expiresAt = kit.latest_calibration?.calibration_expires_at;
    if (!expiresAt) continue;
    const days = daysUntil(expiresAt, now);
    if (days >= 0 && days <= EXPIRING_BANNER_WINDOW_DAYS) {
      expiring.push({ kit, expiresAt, days });
    }
  }
  return expiring.sort((a, b) => a.expiresAt.localeCompare(b.expiresAt));
}

/** In service is the resting good state; every other status is neutral. */
export function kitStatusVariant(status: TestKit["status"]): BadgeVariant {
  return status === "in_service" ? "success" : "default";
}

interface CalibrationBadgeProps {
  expiresAt: string | null;
  nowIso: string;
}

/**
 * The calibration-state cell both tables share: the `kitBadge` rule's variant
 * and key (expired / expiring soon / expires {{date}}) rendered as a Badge —
 * the same vocabulary the kit picker and the old row carried, so one gauge
 * reads the same on every surface.
 */
function CalibrationBadgeCell({ expiresAt, nowIso }: CalibrationBadgeProps) {
  const { t } = useTranslation(["field-work", "common"]);
  const badge = kitBadge({ calibrationExpiresAt: expiresAt }, nowIso);
  return (
    <Badge variant={badge.variant}>
      {expiresAt
        ? t(`field-work:${badge.key}`, { date: formatDateOnly(expiresAt) })
        : t(`field-work:${badge.key}`)}
    </Badge>
  );
}

interface KitTableActions {
  onEdit: (kit: TestKit) => void;
  onAddCalibration: (kit: TestKit) => void;
  onRetire: (kit: TestKit) => void;
}

export interface MyKitColumnOptions {
  /**
   * Lead with the action column instead of trailing with it: below `sm` the
   * table scrolls inside its own box, and trailing verbs would sit past the
   * phone's edge. Every other column keeps its order and its sort declaration.
   */
  actionFirst?: boolean;
}

/**
 * My kits' columns. Every data column declares `enableSorting: true` — the
 * list is client-sorted (the field read paginates nothing), so each header
 * reorders the whole set; the action column opts out on the spot. `last_used`
 * sorts on the wire member itself (a date-time string sorts
 * chronologically in its canonical form), with never-used gauges trailing
 * via `sortUndefined: "last"`.
 */
export function buildMyKitColumns(
  t: TFunction,
  nowIso: string,
  actions: KitTableActions,
  { actionFirst = false }: MyKitColumnOptions = {},
) {
  const data = [
    columnHelper.accessor(kitIdentity, {
      id: "kit",
      header: t("field-work:testKits.columns.kit"),
      enableSorting: true,
      cell: (ctx) => <span className="font-medium">{ctx.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.kit_type ?? "", {
      id: "kit_type",
      header: t("field-work:testKits.columns.type"),
      enableSorting: true,
      cell: (ctx) => ctx.getValue() || <EmptyValue />,
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: t("field-work:testKits.columns.status"),
      enableSorting: true,
      cell: (ctx) => (
        <Badge variant={kitStatusVariant(ctx.getValue())}>
          {t(`field-work:testKits.status.${ctx.getValue()}`)}
        </Badge>
      ),
    }),
    columnHelper.accessor((row) => row.latest_calibration?.calibration_expires_at ?? null, {
      id: "calibration_expires_at",
      header: t("field-work:testKits.columns.calibration"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => <CalibrationBadgeCell expiresAt={ctx.getValue()} nowIso={nowIso} />,
    }),
    columnHelper.accessor((row) => row.last_used_at ?? null, {
      id: "last_used",
      header: t("field-work:testKits.columns.lastUsed"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => {
        const lastUsed = ctx.getValue();
        return lastUsed ? formatDateOnly(lastUsed) : <EmptyValue />;
      },
    }),
  ];
  const actionColumn = columnHelper.display({
    id: "actions",
    header: t("field-work:testKits.columns.actions"),
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="secondary"
          className="min-h-11 min-w-11"
          onClick={() => {
            actions.onEdit(row.original);
          }}
        >
          {t("common:edit")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="min-h-11 min-w-11"
          onClick={() => {
            actions.onAddCalibration(row.original);
          }}
        >
          {t("field-work:myKits.addCalibration")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="min-h-11 min-w-11"
          onClick={() => {
            actions.onRetire(row.original);
          }}
        >
          {t("field-work:myKits.retire")}
        </Button>
      </div>
    ),
  });
  return actionFirst ? [actionColumn, ...data] : [...data, actionColumn];
}

/**
 * Company kits' columns: the Holder column the mock adds, and NO action
 * column at all — the field stack's write verbs (`updateMyTestKit` /
 * `addMyKitCalibration` / the retire delete) admit only the calling tester's
 * OWN kit, so a company row can never carry one and an eternally empty
 * Actions column would only invite the question. The holder sorts on the
 * resolved `holder_name` (the id orders rows by nothing a reader can see) and
 * renders the "Carried by you" chip when the holder is the session's tester.
 */
export function buildCompanyKitColumns(t: TFunction, nowIso: string, holderId: string | null) {
  return [
    columnHelper.accessor(kitIdentity, {
      id: "kit",
      header: t("field-work:testKits.columns.kit"),
      enableSorting: true,
      cell: (ctx) => <span className="font-medium">{ctx.getValue()}</span>,
    }),
    columnHelper.accessor((row) => row.kit_type ?? "", {
      id: "kit_type",
      header: t("field-work:testKits.columns.type"),
      enableSorting: true,
      cell: (ctx) => ctx.getValue() || <EmptyValue />,
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: t("field-work:testKits.columns.status"),
      enableSorting: true,
      cell: (ctx) => (
        <Badge variant={kitStatusVariant(ctx.getValue())}>
          {t(`field-work:testKits.status.${ctx.getValue()}`)}
        </Badge>
      ),
    }),
    columnHelper.accessor((row) => row.latest_calibration?.calibration_expires_at ?? null, {
      id: "calibration_expires_at",
      header: t("field-work:testKits.columns.calibration"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => <CalibrationBadgeCell expiresAt={ctx.getValue()} nowIso={nowIso} />,
    }),
    columnHelper.accessor((row) => row.holder_name ?? "", {
      id: "holder_name",
      header: t("field-work:testKits.columns.holder"),
      enableSorting: true,
      cell: ({ row }) => {
        const kit = row.original;
        if (kit.holder_profile_id !== null && kit.holder_profile_id !== undefined) {
          if (holderId !== null && kit.holder_profile_id === holderId) {
            return <Badge variant="success">{t("field-work:testKits.heldByYou")}</Badge>;
          }
        }
        return kit.holder_name ? <span>{kit.holder_name}</span> : <EmptyValue />;
      },
    }),
    columnHelper.accessor((row) => row.last_used_at ?? null, {
      id: "last_used",
      header: t("field-work:testKits.columns.lastUsed"),
      enableSorting: true,
      sortUndefined: "last",
      cell: (ctx) => {
        const lastUsed = ctx.getValue();
        return lastUsed ? formatDateOnly(lastUsed) : <EmptyValue />;
      },
    }),
  ];
}

/**
 * Test Kits — the tester's selectable gauge set: their own kits plus the ones
 * their company registered, grouped in that order, one table per group.
 *
 * ONLINE-ONLY registry upkeep. Offline the whole screen becomes guidance, Add
 * button included: a kit created against a queue would land after the field
 * work that needed it, and the offline capture picker reads the assignment
 * bundle rather than this list, so nothing here is load-bearing in a basement.
 * The 403 branch precedes the offline one — a tester whose registration ended
 * must read that, not a connectivity note.
 *
 * Only tester-owned kits are mutable on the field stack, so only their rows
 * carry the verbs (Edit / Add calibration / Retire); company rows are
 * read-only here — a company gauge is edited from Company → Kits, where the
 * holder rule lives. Retiring is a soft delete behind a ConfirmDialog — the
 * row stops appearing here while every test it witnessed keeps its record —
 * and it is the one verb on this screen that cannot be undone from the portal.
 */
function TesterTestKitsPage() {
  const { t } = useTranslation(["field-work", "common"]);
  const online = useIsOnline();
  const toast = useToast();
  const myProfileId = useCompanyStore((s) => s.offlineIdentity?.profileId ?? null);

  const query = useMyTestKits();
  const kits = query.data?.items ?? [];
  const deleteKit = useDeleteMyTestKit();

  const [formOpen, setFormOpen] = useState(false);
  const [editKit, setEditKit] = useState<TestKit | null>(null);
  const [retireKit, setRetireKit] = useState<TestKit | null>(null);
  const [calibrationKitId, setCalibrationKitId] = useState<string | null>(null);
  const fromSm = useMediaQuery(BREAKPOINT_SM_QUERY, true);

  const handleRetire = () => {
    if (!retireKit) return;
    deleteKit.mutate(retireKit.id, {
      onSuccess: () => {
        toast.success("field-work:myKits.retired");
        setRetireKit(null);
      },
      onError: (err) => {
        // 404 is the field stack's single answer for "not yours" and "not
        // there": the gauge has left this tester's registry either way, so it
        // reads as the not-found line rather than a failure.
        if (err instanceof ApiError && err.status === 404) {
          toast.error("field-work:myKits.notFound");
          setRetireKit(null);
          return;
        }
        toast.apiError(err instanceof ApiError ? err.code : undefined);
        setRetireKit(null);
      },
    });
  };

  if (query.error instanceof ApiError && query.error.status === 403) {
    return <FieldAccessEnded />;
  }

  if (!online) {
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("field-work:testKits.title")}
          description={t("field-work:myKits.offline")}
        />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="space-y-6 p-4 sm:p-8">
        <Skeleton width="240px" height="36px" />
        <SkeletonTable columns={MY_KITS_COLUMN_COUNT} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex min-h-64 items-center justify-center p-4 sm:p-8">
        <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
      </div>
    );
  }

  const now = new Date();
  const expiring = kitsExpiringWithin(kits, now);
  // The banner's action calibrates the SOONEST gauge the tester can actually
  // write — a company gauge's calibration is the company admin's upkeep.
  const bannerActionKit = expiring.find((entry) => entry.kit.owner_kind === "tester")?.kit ?? null;

  const groups = buildKitGroups(kits);
  const companyLabel =
    groups.find(({ group }) => group === "companies")?.kits[0]?.owner_label ?? "";

  const myColumns = buildMyKitColumns(
    t,
    now.toISOString(),
    {
      onEdit: setEditKit,
      onAddCalibration: (kit) => {
        setCalibrationKitId(kit.id);
      },
      onRetire: setRetireKit,
    },
    { actionFirst: !fromSm },
  );
  const companyColumns = buildCompanyKitColumns(t, now.toISOString(), myProfileId);

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t("field-work:testKits.title")}
        subtitle={t("field-work:testKits.subtitle")}
        actions={
          <Button
            onClick={() => {
              setFormOpen(true);
            }}
          >
            {t("field-work:testKits.add")}
          </Button>
        }
      />

      {expiring.length > 0 && (
        // A window still open, not an alarm already sounded: the table's
        // danger badges carry the expired gauges, the banner carries the ones
        // a renewal today still saves.
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg bg-amber-100 p-4 dark:bg-amber-900"
        >
          <p className="min-w-0 flex-1 text-sm text-amber-800 dark:text-amber-300">
            {t("field-work:testKits.expiringBanner.body", {
              count: expiring.length,
              kits: expiring
                .map((entry) => `${kitIdentity(entry.kit)} (${formatDateOnly(entry.expiresAt)})`)
                .join(", "),
            })}
          </p>
          {bannerActionKit && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setCalibrationKitId(bannerActionKit.id);
              }}
            >
              {t("field-work:myKits.addCalibration")}
            </Button>
          )}
        </div>
      )}

      {groups.length === 0 ? (
        // Online the list IS the server's answer, so the empty state names
        // how kits appear (the sync-to-see advice is offline copy).
        <EmptyState
          title={t("field-work:myKits.empty")}
          description={t("field-work:myKits.emptyDescriptionOnline")}
        />
      ) : (
        groups.map(({ group, kits: groupKits }) => (
          <section key={group} className="space-y-2">
            <h2 className="text-lg font-semibold text-on-surface">
              {group === "companies" ? (
                <>
                  {t("field-work:testKits.groups.companies", { company: companyLabel })}
                  <span className="ml-2 text-sm font-normal text-muted">
                    {t("field-work:testKits.groups.companiesHint")}
                  </span>
                </>
              ) : (
                t("field-work:testKits.groups.mine")
              )}
            </h2>
            <DataTable
              columns={group === "mine" ? myColumns : companyColumns}
              data={groupKits}
              manualSorting={false}
            />
          </section>
        ))
      )}
      <KitFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={() => {
          setFormOpen(false);
        }}
      />
      <KitFormDialog
        open={editKit !== null}
        kit={editKit}
        onClose={() => {
          setEditKit(null);
        }}
        onSaved={() => {
          setEditKit(null);
        }}
      />
      <ConfirmDialog
        open={retireKit !== null}
        title={t("field-work:myKits.retire")}
        message={t("field-work:myKits.retireConfirm")}
        confirmLabel={t("field-work:myKits.retire")}
        cancelLabel={t("common:cancel")}
        variant="danger"
        confirmDisabled={deleteKit.isPending}
        onConfirm={handleRetire}
        onCancel={() => {
          setRetireKit(null);
        }}
      />
      <CalibrationFormDialog
        open={calibrationKitId !== null}
        kitId={calibrationKitId ?? ""}
        onClose={() => {
          setCalibrationKitId(null);
        }}
        onSaved={() => {
          setCalibrationKitId(null);
        }}
      />
    </div>
  );
}
